import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api, formatBRL, formatDateBR, API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { StatusBadge } from "../components/Badges";
import { toast } from "sonner";
import {
  Search, ArrowRight, Download, RefreshCcw, Loader2,
  CheckCircle2, History, X, ArrowUpDown, FolderOpen, LifeBuoy,
} from "lucide-react";
import BatchRecoveryModal from "../components/BatchRecoveryModal";

const FILTERS = [
  { v: "", label: "Todos" },
  { v: "ativo", label: "Ativos" },
  { v: "vence_hoje", label: "Vencendo hoje" },
  { v: "prox_7", label: "7 dias" },
  { v: "atrasado", label: "Atrasados" },
  { v: "quitado", label: "Quitados" },
  { v: "cancelado", label: "Cancelados" },
];

const SORT_OPTIONS = [
  { v: "due_asc", label: "Vencimento (mais próximo)" },
  { v: "due_desc", label: "Vencimento (mais distante)" },
  { v: "capital_desc", label: "Maior capital" },
  { v: "capital_asc", label: "Menor capital" },
  { v: "balance_desc", label: "Maior saldo" },
  { v: "balance_asc", label: "Menor saldo" },
  { v: "client_asc", label: "Cliente (A-Z)" },
  { v: "client_desc", label: "Cliente (Z-A)" },
  { v: "created_desc", label: "Criação (mais recente)" },
  { v: "created_asc", label: "Criação (mais antiga)" },
];

// Rótulos para o histórico do contrato (mesmos usados na ficha do cliente).
const EVENT_LABELS = {
  create: "Contrato criado",
  contract_created: "Contrato criado",
  payment: "Pagamento",
  quittance: "Quitação",
  quittance_self_heal: "Quitação (self-heal)",
  renewal: "Renovação",
  renewed: "Renovação",
  cancel: "Cancelamento",
  socio_transferred: "Troca de sócio",
  interest_percent_changed: "Alteração de % de juros",
  recovery_entered: "Entrou em recuperação",
};

function sortItems(items, sort) {
  const dir = sort.endsWith("_desc") ? -1 : 1;
  const key = sort.replace(/_(asc|desc)$/, "");
  const arr = [...items];
  arr.sort((a, b) => {
    let av, bv;
    switch (key) {
      case "due":     av = a.due_date || ""; bv = b.due_date || ""; break;
      case "capital": av = a.capital || 0; bv = b.capital || 0; break;
      case "balance": av = a.balance || 0; bv = b.balance || 0; break;
      case "client":  av = (a.client_name || "").toLowerCase(); bv = (b.client_name || "").toLowerCase(); break;
      case "created": av = a.created_at || ""; bv = b.created_at || ""; break;
      default: return 0;
    }
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });
  return arr;
}

// ---------- Modal: Quitar ----------
function QuittanceModal({ row, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const capital = Number(row.capital || 0);
  const juros = Math.max(Number(row.total_due || 0) - capital, 0);
  const balance = Number(row.balance || 0);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/contracts/${row.contract_id}/quittance`, {});
      toast.success(`Contrato quitado — ${row.client_name}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao quitar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="quittance-modal">
      <div className="fc-card w-full max-w-md my-8 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
              <CheckCircle2 size={16} className="text-emerald-300"/> Confirmar quitação
            </h3>
            <p className="text-[12px] text-[#A3A3A3] mt-1" data-testid="quittance-client">{row.client_name}</p>
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-white" data-testid="quittance-close">
            <X size={16}/>
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-[#737373]">Capital</div>
            <div className="font-mono-tab text-sm mt-0.5" data-testid="quittance-capital">{formatBRL(capital)}</div>
          </div>
          <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-[#737373]">Juros</div>
            <div className="font-mono-tab text-sm mt-0.5 text-[#D4AF37]" data-testid="quittance-juros">{formatBRL(juros)}</div>
          </div>
          <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-[#737373]">Saldo atual</div>
            <div className="font-mono-tab text-sm mt-0.5 text-yellow-300" data-testid="quittance-balance">{formatBRL(balance)}</div>
          </div>
          <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-emerald-300">Valor da quitação</div>
            <div className="font-mono-tab text-sm mt-0.5 text-emerald-300" data-testid="quittance-value">{formatBRL(balance)}</div>
          </div>
        </div>

        <p className="text-[11.5px] text-[#737373] mb-4">
          Ao confirmar, o contrato será marcado como <strong className="text-emerald-300">quitado</strong> e os módulos de Tesouraria, Sócios, Recuperação e Dashboard serão atualizados automaticamente.
        </p>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="fc-btn-ghost text-xs" data-testid="quittance-cancel">
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="fc-btn-gold flex items-center gap-1.5 text-xs"
            data-testid="quittance-confirm"
          >
            {saving ? <Loader2 size={12} className="animate-spin"/> : <CheckCircle2 size={12}/>}
            Confirmar Quitação
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Modal: Histórico ----------
function HistoryModal({ row, onClose }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get(`/contracts/${row.contract_id}/history`)
      .then(({ data }) => { if (alive) setItems(data); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [row.contract_id]);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="history-modal">
      <div className="fc-card w-full max-w-2xl my-8 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
              <History size={16} className="text-cyan-300"/> Histórico do contrato
            </h3>
            <p className="text-[12px] text-[#A3A3A3] mt-1">{row.client_name} · #{(row.contract_id || "").slice(0,8)}</p>
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-white" data-testid="history-close">
            <X size={16}/>
          </button>
        </div>

        {loading ? (
          <div className="text-[13px] text-[#737373] flex items-center gap-2">
            <Loader2 size={14} className="animate-spin"/> Carregando…
          </div>
        ) : !items || items.length === 0 ? (
          <div className="text-[13px] text-[#737373] italic">Sem eventos registrados.</div>
        ) : (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-2">
            {items.map((h, idx) => (
              <div key={idx} className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-[12.5px] font-medium">{EVENT_LABELS[h.event] || h.event}</div>
                  <div className="text-[10.5px] text-[#737373] font-mono-tab">
                    {h.created_at ? new Date(h.created_at).toLocaleString("pt-BR") : "—"}
                  </div>
                </div>
                <div className="text-[11.5px] text-[#A3A3A3] mt-1">
                  {h.data?.amount != null && (
                    <span className="text-[#D4AF37] font-mono-tab mr-3">{formatBRL(h.data.amount)}</span>
                  )}
                  {h.event === "socio_transferred" && (
                    <span className="text-amber-300">{h.data?.from_socio_name || "—"} → {h.data?.to_socio_name || "—"}</span>
                  )}
                  {h.event === "interest_percent_changed" && (
                    <span className="font-mono-tab text-[#D4AF37]">{h.data?.old_interest_percent}% → {h.data?.new_interest_percent}%</span>
                  )}
                  {h.data?.new_due_date && (
                    <span className="ml-3 text-[#737373]">novo venc. {formatDateBR(h.data.new_due_date)}</span>
                  )}
                  {h.data?.balance_after !== undefined && (
                    <span className="ml-3 text-[#737373]">saldo → {formatBRL(h.data.balance_after)}</span>
                  )}
                </div>
                <div className="text-[10.5px] text-[#737373] mt-1">
                  {h.user_email || "sistema"}
                  {h.data?.reason ? ` · Motivo: ${h.data.reason}` : ""}
                  {h.data?.notes ? ` · ${h.data.notes}` : ""}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end mt-4">
          <button onClick={onClose} className="fc-btn-ghost text-xs">Fechar</button>
        </div>
      </div>
    </div>
  );
}

// ---------- Row Actions (extraído para não recriar em cada render) ----------
function RowActions({ r, canEdit, busy, onRenew, onQuittance, onHistory }) {
  const showAction = canEdit && r.status === "ativo";
  const btn = "inline-flex items-center justify-center w-7 h-7 rounded-md border border-[#1f1f1f] hover:border-[#D4AF37]/50 hover:bg-[#141414] transition-colors";
  return (
    <div className="flex items-center gap-1 justify-end whitespace-nowrap">
      <Link
        to={`/clientes/${r.client_id}`}
        className={`${btn} text-[#D4AF37]`}
        data-testid={`contract-open-${r.contract_id}`}
        title="Abrir cliente"
      >
        <FolderOpen size={13}/>
      </Link>
      {showAction && (
        <button
          onClick={() => onRenew(r)}
          disabled={busy === r.contract_id+":renew"}
          className={`${btn} text-[#D4AF37]`}
          title="Renovar contrato"
          data-testid={`btn-renew-${r.contract_id}`}
        >
          {busy === r.contract_id+":renew" ? <Loader2 size={13} className="animate-spin"/> : <RefreshCcw size={13}/>}
        </button>
      )}
      {showAction && (
        <button
          onClick={() => onQuittance(r)}
          className={`${btn} text-emerald-300 hover:border-emerald-500/50`}
          title="Quitar contrato"
          data-testid={`btn-quittance-${r.contract_id}`}
        >
          <CheckCircle2 size={13}/>
        </button>
      )}
      <button
        onClick={() => onHistory(r)}
        className={`${btn} text-cyan-300 hover:border-cyan-500/50`}
        title="Ver histórico"
        data-testid={`btn-history-${r.contract_id}`}
      >
        <History size={13}/>
      </button>
      <a
        href={`${API}/contracts/${r.contract_id}/pdf`}
        target="_blank"
        rel="noreferrer"
        className={`${btn} text-[#A3A3A3] hover:text-white`}
        title="PDF"
        data-testid={`btn-pdf-${r.contract_id}`}
      >
        <Download size={13}/>
      </a>
    </div>
  );
}

export default function ContratosPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "gestora";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [portfolio, setPortfolio] = useState("all"); // all | normal | recovery — iter51
  const [search, setSearch] = useState("");
  const [fIndicator, setFIndicator] = useState("");
  const [fSocio, setFSocio] = useState("");
  const [indicators, setIndicators] = useState([]);
  const [socios, setSocios] = useState([]);
  const [busy, setBusy] = useState(null);
  const [sort, setSort] = useState("due_asc");
  const [quittanceRow, setQuittanceRow] = useState(null);
  const [historyRow, setHistoryRow] = useState(null);
  // iter71 — Seleção em lote para Recuperação
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchOpen, setBatchOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.filter = filter;
      if (portfolio && portfolio !== "all") params.portfolio = portfolio;
      if (search) params.search = search;
      if (fIndicator) params.indicator_id = fIndicator;
      if (fSocio) params.socio_id = fSocio;
      const { data } = await api.get("/contracts", { params });
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (user?.role !== "socio") {
      api.get("/indicators").then(r=>setIndicators(r.data)).catch(()=>{});
      // Sócios só é acessível para admin — gestora recebe 403.
      if (user?.role === "admin") {
        api.get("/socios").then(r=>setSocios(r.data)).catch(()=>{});
      }
    }
  }, [user]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [filter, portfolio, search, fIndicator, fSocio]);

  const sortedItems = useMemo(() => sortItems(items, sort), [items, sort]);

  // iter71 — IDs elegíveis para recuperação (contratos ativos, excluindo os já em recovery)
  const selectableIds = useMemo(() =>
    new Set(
      sortedItems
        .filter(r => r.status === "ativo" && !r.in_recovery)
        .map(r => r.contract_id)
    ),
    [sortedItems]
  );
  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };
  const toggleAll = () => {
    const allSelected = selectableIds.size > 0 && [...selectableIds].every(id => selectedIds.has(id));
    if (allSelected) {
      const next = new Set(selectedIds);
      selectableIds.forEach(id => next.delete(id));
      setSelectedIds(next);
    } else {
      const next = new Set(selectedIds);
      selectableIds.forEach(id => next.add(id));
      setSelectedIds(next);
    }
  };
  // Reset ao mudar filtros
  useEffect(() => { setSelectedIds(new Set()); }, [filter, portfolio, search, fIndicator, fSocio]);

  const quickRenew = async (r) => {
    if (!window.confirm(`Renovar contrato de ${r.client_name}?`)) return;
    setBusy(r.contract_id+":renew");
    try {
      await api.post(`/contracts/${r.contract_id}/renew`, {});
      toast.success("Contrato renovado");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro ao renovar"); }
    finally { setBusy(null); }
  };

  return (
    <Layout>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Operação</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Contratos</h1>
        <p className="text-[#A3A3A3] text-sm mt-1">{sortedItems.length} resultado(s)</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="contract-filters">
        {FILTERS.map(f => (
          <button key={f.v} onClick={()=>setFilter(f.v)} className={`px-3.5 py-1.5 rounded-md text-[12.5px] border transition-colors ${filter === f.v ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#262626] text-[#A3A3A3] hover:border-[#3a3a3a]"}`} data-testid={`filter-${f.v||"all"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* iter51 — Filtro de Portfolio: Todos / Normais / Recuperação */}
      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="portfolio-filter">
        <span className="text-[10.5px] uppercase tracking-[0.14em] text-[#525252] mr-1">Carteira</span>
        {[
          { v: "all", label: "Todos", color: "gold" },
          { v: "normal", label: "Normais", color: "emerald" },
          { v: "recovery", label: "Recuperação", color: "amber" },
        ].map(p => {
          const active = portfolio === p.v;
          const activeStyle = p.color === "gold"
            ? "bg-[#D4AF37] text-black border-[#D4AF37]"
            : p.color === "emerald"
            ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/50"
            : "bg-amber-500/15 text-amber-200 border-amber-500/50";
          return (
            <button
              key={p.v}
              onClick={() => setPortfolio(p.v)}
              className={`px-3 py-1.5 rounded-md text-[12px] border transition-colors ${
                active ? activeStyle : "border-[#262626] text-[#737373] hover:border-[#3a3a3a]"
              }`}
              data-testid={`portfolio-filter-${p.v}`}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      <div className="fc-card p-4 mb-5 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]"/>
          <input className="fc-input pl-9" placeholder="Buscar nome, CPF..." value={search} onChange={e=>setSearch(e.target.value)} data-testid="search-input"/>
        </div>
        {user?.role !== "socio" && (
          <>
            <select className="fc-input min-w-[160px]" value={fIndicator} onChange={e=>setFIndicator(e.target.value)} data-testid="filter-indicator">
              <option value="">Indicadores</option>
              {indicators.map(i=>(<option key={i.id} value={i.id}>{i.name}</option>))}
            </select>
            {user?.role === "admin" && (
              <select className="fc-input min-w-[160px]" value={fSocio} onChange={e=>setFSocio(e.target.value)} data-testid="filter-socio">
                <option value="">Sócios</option>
                {socios.map(s=>(<option key={s.id} value={s.id}>{s.name}</option>))}
              </select>
            )}
          </>
        )}
        <div className="flex items-center gap-1.5">
          <ArrowUpDown size={13} className="text-[#737373]"/>
          <select
            className="fc-input min-w-[210px]"
            value={sort}
            onChange={e=>setSort(e.target.value)}
            data-testid="sort-select"
            title="Ordenar por"
          >
            {SORT_OPTIONS.map(o => (
              <option key={o.v} value={o.v}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="fc-card overflow-hidden">
        {/* Tabela desktop */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm" data-testid="contracts-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#A3A3A3] border-b border-[#1f1f1f]">
                {canEdit && (
                  <th className="py-3 px-3 w-9">
                    <input
                      type="checkbox"
                      aria-label="Selecionar todos filtrados"
                      checked={selectableIds.size > 0 && [...selectableIds].every(id => selectedIds.has(id))}
                      onChange={toggleAll}
                      className="accent-[#D4AF37] cursor-pointer"
                      data-testid="contracts-select-all"
                    />
                  </th>
                )}
                <th className="py-3 px-4">Cliente</th>
                <th className="py-3 px-4">Indicador</th>
                {user?.role !== "socio" && <th className="py-3 px-4">Sócio</th>}
                <th className="py-3 px-4">Capital</th>
                <th className="py-3 px-4">Juros</th>
                <th className="py-3 px-4">Total</th>
                <th className="py-3 px-4">Pago</th>
                <th className="py-3 px-4">Saldo</th>
                <th className="py-3 px-4">Vencimento</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 text-right pr-6">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && [1,2,3].map(i=>(<tr key={i} className="border-b border-[#1f1f1f]"><td colSpan={canEdit ? 12 : 11} className="py-3"><div className="h-5 shimmer rounded"/></td></tr>))}
              {!loading && sortedItems.length === 0 && (<tr><td colSpan={canEdit ? 12 : 11} className="py-12 text-center text-[#737373]">Nenhum contrato.</td></tr>)}
              {!loading && sortedItems.map(r => (
                <tr key={r.contract_id} className={`border-b border-[#1f1f1f] hover:bg-[#121212] ${r.in_recovery ? "bg-amber-500/[0.04]" : r.is_overdue ? "bg-red-500/5" : ""}`} data-testid={`contract-row-${r.contract_id}`}>
                  {canEdit && (
                    <td className="py-3 px-3">
                      {selectableIds.has(r.contract_id) ? (
                        <input
                          type="checkbox"
                          aria-label={`Selecionar ${r.client_name}`}
                          checked={selectedIds.has(r.contract_id)}
                          onChange={() => toggleOne(r.contract_id)}
                          className="accent-[#D4AF37] cursor-pointer"
                          data-testid={`contract-select-${r.contract_id}`}
                        />
                      ) : null}
                    </td>
                  )}
                  <td className="py-3 px-4">
                    <div className="font-medium flex items-center gap-2">
                      {r.client_name}
                      {r.in_recovery && (
                        <span className="badge-base text-[9.5px] bg-amber-500/15 text-amber-200 border-amber-500/40 tracking-wider" data-testid={`row-recovery-badge-${r.contract_id}`}>
                          RECUPERAÇÃO
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-[#737373] font-mono-tab">{r.cpf && r.cpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/,"$1.$2.$3-$4")}</div>
                  </td>
                  <td className="py-3 px-4 text-[#A3A3A3]">{r.indicator_name || "—"}</td>
                  {user?.role !== "socio" && <td className="py-3 px-4 text-[#A3A3A3]">{r.socio_name || "—"}</td>}
                  <td className="py-3 px-4 font-mono-tab">{formatBRL(r.capital)}</td>
                  <td className="py-3 px-4 font-mono-tab text-[#D4AF37]">{r.interest_percent}%</td>
                  <td className="py-3 px-4 font-mono-tab">{formatBRL(r.total_due)}</td>
                  <td className="py-3 px-4 font-mono-tab text-emerald-300">{formatBRL(r.total_paid)}</td>
                  <td className={`py-3 px-4 font-mono-tab ${r.balance > 0 ? "text-yellow-300" : "text-emerald-300"}`}>{formatBRL(r.balance)}</td>
                  <td className={`py-3 px-4 ${r.is_overdue ? "text-red-300" : ""}`}>{r.due_date ? formatDateBR(r.due_date) : "—"}</td>
                  <td className="py-3 px-4"><StatusBadge status={r.status === "ativo" ? "contratado" : r.status === "quitado" ? "pago" : r.status === "cancelado" ? "negado" : r.status}/></td>
                  <td className="py-3 px-4 text-right pr-6">
                    <RowActions r={r} canEdit={canEdit} busy={busy} onRenew={quickRenew} onQuittance={setQuittanceRow} onHistory={setHistoryRow}/>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Cards mobile */}
        <div className="md:hidden divide-y divide-[#1f1f1f]" data-testid="contracts-cards">
          {loading && [1,2,3].map(i=>(<div key={i} className="p-4"><div className="h-20 shimmer rounded"/></div>))}
          {!loading && sortedItems.length === 0 && (<div className="py-12 text-center text-[#737373] text-sm">Nenhum contrato.</div>)}
          {!loading && sortedItems.map(r => (
            <div key={r.contract_id} className={`p-4 ${r.in_recovery ? "bg-amber-500/[0.04]" : r.is_overdue ? "bg-red-500/5" : ""}`} data-testid={`contract-card-${r.contract_id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-[14.5px] truncate flex items-center gap-2">
                    {r.client_name}
                    {r.in_recovery && (
                      <span className="badge-base text-[9.5px] bg-amber-500/15 text-amber-200 border-amber-500/40 tracking-wider">
                        RECUPERAÇÃO
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#737373] mt-0.5">{r.indicator_name || "—"}{user?.role !== "socio" && r.socio_name ? ` · ${r.socio_name}` : ""}</div>
                </div>
                <StatusBadge status={r.status === "ativo" ? "contratado" : r.status === "quitado" ? "pago" : r.status === "cancelado" ? "negado" : r.status}/>
              </div>
              <div className="grid grid-cols-3 gap-2 text-[12px] mt-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#737373]">Capital</div>
                  <div className="font-mono-tab mt-0.5">{formatBRL(r.capital)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#737373]">Saldo</div>
                  <div className={`font-mono-tab mt-0.5 ${r.balance > 0 ? "text-yellow-300" : "text-emerald-300"}`}>{formatBRL(r.balance)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#737373]">Vencimento</div>
                  <div className={`mt-0.5 ${r.is_overdue ? "text-red-300" : ""}`}>{r.due_date ? formatDateBR(r.due_date) : "—"}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3 pt-3 border-t border-[#1f1f1f] flex-wrap">
                <RowActions r={r} canEdit={canEdit} busy={busy} onRenew={quickRenew} onQuittance={setQuittanceRow} onHistory={setHistoryRow}/>
              </div>
            </div>
          ))}
        </div>
      </div>

      {quittanceRow && (
        <QuittanceModal
          row={quittanceRow}
          onClose={() => setQuittanceRow(null)}
          onDone={() => { setQuittanceRow(null); load(); }}
        />
      )}
      {historyRow && (
        <HistoryModal row={historyRow} onClose={() => setHistoryRow(null)}/>
      )}

      {/* iter71 — Barra flutuante de seleção em lote */}
      {canEdit && selectedIds.size > 0 && !batchOpen && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 fc-card border-[#D4AF37]/40 shadow-2xl px-5 py-3 flex items-center gap-4" data-testid="contracts-batch-bar">
          <div className="text-[12.5px]">
            <span className="text-[#D4AF37] font-semibold" data-testid="contracts-batch-count">
              {selectedIds.size}
            </span> contrato(s) selecionado(s)
          </div>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-[11.5px] text-[#A3A3A3] hover:text-white underline underline-offset-2"
          >
            Limpar
          </button>
          <button
            onClick={() => setBatchOpen(true)}
            className="fc-btn-gold text-xs flex items-center gap-1.5"
            data-testid="contracts-batch-send-recovery"
          >
            <LifeBuoy size={13}/> Enviar para Recuperação
          </button>
        </div>
      )}
      {batchOpen && (
        <BatchRecoveryModal
          items={[...selectedIds].map(id => ({ record_type: "contract", record_id: id }))}
          onClose={() => setBatchOpen(false)}
          onDone={() => { setBatchOpen(false); setSelectedIds(new Set()); load(); }}
        />
      )}
    </Layout>
  );
}
